import React, { useEffect, useState } from "react";
import { Modal } from "../../../components/modal/Modal";
import { Trans, useTranslation } from "react-i18next";
import { Flex } from "../../../components/Flex";
import { Entry } from "../../../model/entry/Entry";
import { useAppDispatch, useAppSelector } from "../../../redux/store";
import { entrySliceSelectors } from "../../../redux/entry/entry.selector";
import { moduleSliceSelectors } from "../../../redux/module/module.selector";
import { Controller, useForm } from "react-hook-form";
import { CreateModuleInstanceAttachmentDto } from "../../../model/module/dto/module-instance-attachment/CreateModuleInstanceAttachmentDto";
import { BaseModuleInstance } from "../../../model/module/types/module-instance/BaseModuleInstance";
import { userSliceSelectors } from "../../../redux/user/user.selector";
import { AtiraToast } from "../../../utils/AtiraToast";
import { PageMeta } from "../../../model/shared/meta/PageMeta";
import { entryActions } from "../../../redux/entry/entry.slice";
import { Button } from "../../../components/Button";
import { useLocation } from "react-router-dom";
import { ModuleDefinition } from "../../../model/module/types/module-definition/ModuleDefinition";
import { AtiraSelect } from "../../../components/AtiraSelect";
import { moduleActions } from "../../../redux/module/module.slice";
import { onSetTypedCRUDErrors } from "../../../utils/onSetTypedCRUDErrors";
import { Info } from "../../../components/Info";
import { Input } from "../../../components/Input";
import { Lengths } from "../../../model/shared/enum/Lengths.enum";
import { ModuleDefinitionKind } from "../../../model/module/types/module-definition/ModuleDefinitionKind";
import { appActions } from "../../../redux/app/app.slice";
import { SearchKind } from "../../../model/search/types/SearchKind.enum";
import { SearchInputEntries } from "../../../components/InputSearchEntries";

type ModuleInstanceAttachmentCreateModalProps = {
  open: boolean;
  onClose: VoidFunction;
  moduleInstance?: BaseModuleInstance | null;
  moduleDefinition?: ModuleDefinition | null;
  entry?: Entry | null;
};

export const ModuleInstanceAttachmentCreateModal: React.FC<
  ModuleInstanceAttachmentCreateModalProps
> = ({ open = false, onClose, moduleInstance, moduleDefinition, entry }) => {
  const [moduleInstanceOptions, setModuleInstanceOptions] = useState<
    { label: string; value: string }[]
  >([]);
  const [loading, setLoading] = useState(false);

  const dispatch = useAppDispatch();
  const { pathname } = useLocation();
  const { t } = useTranslation();
  const { setValue, watch, reset, control, setError, getValues } =
    useForm<CreateModuleInstanceAttachmentDto>();

  const latestEntries = useAppSelector(entrySliceSelectors.selectLatestEntries);
  const moduleInstancesPage = useAppSelector(
    moduleSliceSelectors.selectInstancesPage,
  );
  const moduleInstancesPageSize = useAppSelector(
    moduleSliceSelectors.selectInstancesPageSize,
  );
  const userId = useAppSelector(userSliceSelectors.selectLoggedInUserId)!;

  const parentModule = useAppSelector((s) =>
    moduleSliceSelectors.selectModuleDefinitionById(
      s,
      moduleInstance?.parentId || "",
    ),
  );

  const [entryId, name, moduleInstanceId] = watch([
    "entryId",
    "name",
    "moduleInstanceId",
  ]);

  const selectedEntryId = entryId || entry?._id;

  const isOnEntriesPage = pathname.includes("/entries");

  const isButtonDisabled =
    (isOnEntriesPage && !moduleInstanceId) ||
    (!isOnEntriesPage && !selectedEntryId) ||
    !name?.trim()?.length;

  const localOnClose = () => {
    onClose();
    reset({
      entryId: "",
      moduleInstanceId: "",
      name: "",
      kind: ModuleDefinitionKind.FIXED,
      userId,
      moduleDefinitionId: moduleDefinition?._id || "",
    });
  };

  const onFetchEntries = async (keyword: string) => {
    try {
      if (!keyword.trim().length) {
        return latestEntries;
      }

      const { results: entries } = await dispatch(
        appActions.search({
          userId,
          keyword,
          kind: SearchKind.ENTRY,
        }),
      ).unwrap();

      return (entries || []) as Entry[];
    } catch (e: any) {
      AtiraToast.apiError(e);
      console.log(e);
      return [];
    }
  };

  const onAttach = async () => {
    try {
      if (!selectedEntryId) {
        return;
      }

      setLoading(true);

      const moduleInstanceId = getValues("moduleInstanceId");

      await dispatch(
        moduleActions.attachModuleInstanceToEntry({
          entryId: selectedEntryId,
          moduleInstanceId,
          moduleDefinitionId:
            moduleDefinition?._id || moduleInstance?.parentId || "",
          userId,
          kind: moduleDefinition?.kind || ModuleDefinitionKind.FIXED,
          name: getValues("name"),
        }),
      ).unwrap();

      if (isOnEntriesPage) {
        await dispatch(
          entryActions.getEntryById({ entryId: selectedEntryId, userId }),
        ).unwrap();
      } else {
        await dispatch(
          moduleActions.getModuleInstances({
            keyword: "",
            moduleDefinitionId:
              moduleDefinition?._id || moduleInstance?.parentId || "",
            meta: PageMeta.create({
              page: moduleInstancesPage - 1,
              count: moduleInstancesPageSize,
            }),
            userId,
          }),
        ).unwrap();
      }

      localOnClose();
      AtiraToast.success(t("modules.attach.create.success"));
    } catch (e) {
      console.log(e);
      AtiraToast.apiError(e);
      onSetTypedCRUDErrors(e, setError);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (moduleInstance) {
      reset({
        entryId: entry?._id,
        moduleInstanceId: moduleInstance._id,
        userId,
      });
    }
  }, [entry?._id, moduleInstance, userId, reset]);

  useEffect(() => {
    if (!moduleDefinition) {
      return;
    }

    let isMounted = true;

    const fetchOptions = async () => {
      try {
        const { data } = await dispatch(
          moduleActions.getModuleInstances({
            moduleDefinitionId: moduleDefinition._id,
            userId,
            keyword: "",
            meta: PageMeta.create({ page: 0, count: 1000 }),
          }),
        ).unwrap();

        if (isMounted) {
          setModuleInstanceOptions(
            data.map((instance) => {
              return {
                label: instance?.name || "",
                value: instance._id,
              };
            }),
          );
        }
      } catch (e) {
        console.log(e);
      }
    };

    fetchOptions();

    return () => {
      isMounted = false;
    };
  }, [moduleDefinition, userId, dispatch]);

  return (
    <Modal
      title={t("modules.attachToEntryModal.title")}
      open={open}
      onClose={localOnClose}
    >
      <Flex width={"100%"} flexDirection="column" gap="m">
        <Flex flexDirection="column" gap="s">
          <Info
            info={
              <Trans
                i18nKey={"modules.attachToEntryModal.info-1"}
                components={[<strong />]}
                values={{
                  module: moduleDefinition?.name || parentModule?.name,
                  entry:
                    entry?.fields?.email ||
                    entry?.fields?.phone ||
                    entry?.fields?.name,
                }}
              />
            }
          />
          <Info info={t("modules.attachToEntryModal.info-2")} />
        </Flex>

        <Controller
          control={control}
          name="name"
          rules={{
            required: {
              value: true,
              message: t("common.fieldRequired"),
            },
            maxLength: {
              value: Lengths.DESCRIPTION,
              message: t("error.length.less", {
                length: Lengths.DESCRIPTION,
                name: t("common.name"),
              }),
            },
          }}
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <Input
              title={t("common.name")}
              value={value}
              onChange={onChange}
              valid={!error}
              errorMessage={error?.message}
              maxLength={Lengths.DESCRIPTION}
              info={t("modules.instance.create.name.info")}
              required
            />
          )}
        />

        {!isOnEntriesPage ? (
          <SearchInputEntries
            title={t("modules.attachToEntryModal.description")}
            info={t("modules.attachToEntryModal.entryId.info")}
            onChange={(e: any) => {
              setValue("entryId", e?._id || "");
            }}
            fetchOptions={onFetchEntries}
            required
            isSearchVisible={open}
          />
        ) : (
          <Controller
            control={control}
            name="moduleInstanceId"
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <AtiraSelect
                title={t("modules.attachToEntryModal.selectInstance.title")}
                value={moduleInstanceOptions?.find((m) => m.value === value)}
                onChange={onChange}
                options={moduleInstanceOptions}
                valid={!error}
                errorMessage={error?.message}
                required
              />
            )}
          />
        )}

        <Button
          onClick={onAttach}
          loading={loading}
          disabled={isButtonDisabled}
          margin="none"
        >
          {t("modules.attachToEntryModal.button")}
        </Button>
      </Flex>
    </Modal>
  );
};
